import { useMutation } from '@tanstack/react-query';
import { AlertCircle, BadgeCheck, Fingerprint, Loader2, ShieldQuestion } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';

interface Identidad {
  identificacion: string;
  tipo: string;
  nombre: string;
  fechaNacimiento?: string;
  condicion?: string;
  fallecido: boolean;
}

export function ComprobacionEnElRegistroCivil({
  identificacion,
  alConstarCondicion,
}: {
  identificacion: string;

  alConstarCondicion: (condicion: string) => void;
}) {
  const { puede } = useSesion();

  const consulta = useMutation({
    mutationFn: (cedula: string) => api.get<Identidad>(`/identidades/${cedula}`),
    onSuccess: (i) => {
      if (i.condicion) {
        alConstarCondicion(i.condicion);
      }
    },
  });

  if (identificacion.length !== 10 || !puede('identidades.consultar')) {
    return null;
  }

  const i = consulta.data;

  return (
    <div className="rounded-lg border border-armazon-200 bg-armazon-50/60 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex min-w-0 flex-1 items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
          <Fingerprint className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
          Lo que consta en el Registro Civil
        </span>

        <button
          type="button"
          onClick={() => consulta.mutate(identificacion)}
          disabled={consulta.isPending}
          className="flex h-8 shrink-0 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-3 text-[11px] font-bold uppercase tracking-wide text-armazon-600 transition-colors hover:border-marca-300 hover:text-marca-700 disabled:opacity-50"
        >
          {consulta.isPending ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <ShieldQuestion className="size-3.5" aria-hidden />
          )}
          {consulta.isSuccess ? 'Volver a comprobar' : 'Comprobar'}
        </button>
      </div>

      {consulta.isError && (
        <p role="alert" className="mt-2 text-[11px] uppercase text-alerta">
          {consulta.error instanceof ErrorDeApi
            ? consulta.error.message
            : 'No se pudo consultar el Registro Civil'}
        </p>
      )}

      {i && (
        <div className="mt-3 space-y-2">
          {i.fallecido && (
            <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-bold uppercase text-red-800">
              <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
              Consta su defunción. Revise a nombre de quién está el servicio antes de seguir.
            </p>
          )}

          <Hallazgo
            rotulo="Condición declarada"
            valor={i.condicion}
            respalda="Respalda una exoneración por discapacidad"
            vacio="El Registro Civil no declara ninguna condición"
          />

          <Hallazgo
            rotulo="Edad"
            valor={edad(i.fechaNacimiento)}
            respalda={
              cumpleTerceraEdad(i.fechaNacimiento)
                ? 'Respalda una exoneración por tercera edad'
                : undefined
            }
            vacio="Sin fecha de nacimiento"
          />

          {i.condicion && (
            <p className="text-[10px] uppercase leading-relaxed text-marca-700">
              La condición se ha copiado a las observaciones de la exoneración. Bórrela de ahí si no
              procede.
            </p>
          )}

          <p className="text-[10px] uppercase leading-relaxed text-armazon-500">
            Esto corrobora, no sustituye: el carné del CONADIS y el certificado médico siguen siendo
            el respaldo. <b>Que aquí no conste nada no significa que no le corresponda</b>— el
            Registro Civil no declara todas las discapacidades ni ninguna enfermedad catastrófica.
          </p>
        </div>
      )}
    </div>
  );
}

function Hallazgo({
  rotulo,
  valor,
  respalda,
  vacio,
}: {
  rotulo: string;
  valor: string | undefined;
  respalda?: string | undefined;
  vacio: string;
}) {
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 text-[11px] uppercase">
      <span className="font-semibold text-armazon-500">{rotulo}:</span>
      <span className={valor ? 'font-bold text-armazon-800' : 'text-armazon-400'}>
        {valor ?? vacio}
      </span>
      {valor && respalda && (
        <span className="flex items-center gap-1 rounded-md bg-marca-50 px-1.5 py-0.5 text-[10px] font-bold text-marca-700">
          <BadgeCheck className="size-3" aria-hidden />
          {respalda}
        </span>
      )}
    </p>
  );
}

function edad(fechaNacimiento: string | undefined): string | undefined {
  const anios = cuantosAnios(fechaNacimiento);
  return anios == null ? undefined : `${anios} años`;
}

function cumpleTerceraEdad(fechaNacimiento: string | undefined): boolean {
  const anios = cuantosAnios(fechaNacimiento);
  return anios != null && anios >= 65;
}

function cuantosAnios(fechaNacimiento: string | undefined): number | null {
  if (!fechaNacimiento) {
    return null;
  }
  const [anio, mes, dia] = fechaNacimiento.split('-').map(Number);
  if (!anio || !mes || !dia) {
    return null;
  }
  const hoy = new Date();
  let anios = hoy.getFullYear() - anio;

  const yaCumplio =
    hoy.getMonth() + 1 > mes || (hoy.getMonth() + 1 === mes && hoy.getDate() >= dia);
  if (!yaCumplio) {
    anios -= 1;
  }
  return anios >= 0 && anios < 130 ? anios : null;
}
